import React, { useState, useEffect } from 'react'
import {
	Button,
	IconButton,
	Popover,
	Switch,
	FormControlLabel,
	Box,
	Typography,
	Paper,
	TextField,
} from '@mui/material'
import { useTheme as useMuiTheme } from '@mui/material/styles'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SettingsIcon from '@mui/icons-material/Settings'
import Brightness4Icon from '@mui/icons-material/Brightness4'
import Brightness7Icon from '@mui/icons-material/Brightness7'
import { loadTopListsFromLocalStorage } from './Settings'
import Settings from './Settings'
import { useTheme } from './ThemeProvider'

const SelectPage = () => {
	const muiTheme = useMuiTheme()
	const { isDarkMode, toggleTheme } = useTheme()
	const [topLists, setTopLists] = useState([])
	const [settingsAnchor, setSettingsAnchor] = useState(null)

	const defaultState = {
		itemSearchTerm: '',
		outItemInput: '',
		selectedSource: null,
		selectedItems: [],
		outItems: [],
		showDescription: false,
		itemDisplayFreeze: true,
	}

	const [state, setState] = useState(defaultState)

	// Load TopLists from localStorage
	const loadTopLists = () => {
		const lists = loadTopListsFromLocalStorage()
		setTopLists(lists)
	}

	useEffect(() => {
		loadTopLists()
	}, [])

	useEffect(() => {
		const storedItems = JSON.parse(localStorage.getItem('selectedItems')) || []
		const storedSource = localStorage.getItem('selectedSource') || null
		const storedOutItems = JSON.parse(localStorage.getItem('outItems')) || []
		setState((prev) => ({
			...prev,
			selectedItems: storedItems,
			outItems: storedOutItems,
			selectedSource: storedSource,
		}))
	}, [])

	useEffect(() => {
		if (state.itemDisplayFreeze) {
			return
		}
		localStorage.setItem('selectedItems', JSON.stringify(state.selectedItems))
	}, [state.selectedItems, state.itemDisplayFreeze])

	useEffect(() => {
		if (state.selectedSource) {
			localStorage.setItem('selectedSource', state.selectedSource)
		}
	}, [state.selectedSource])

	useEffect(() => {
		if (state.itemDisplayFreeze) {
			return
		}
		localStorage.setItem('outItems', JSON.stringify(state.outItems))
	}, [state.outItems, state.itemDisplayFreeze])

	// Get current source data from TopLists using key
	const currentTopList = topLists.find((list) => list.key === state.selectedSource)
	const currentSourceData = currentTopList ? currentTopList.values || [] : []

	const filteredData = currentSourceData.filter((item) =>
		item.name.toLowerCase().includes(state.itemSearchTerm.toLowerCase())
	)

	const handleSelectSource = (sourceKey) => {
		setState({
			...state,
			selectedSource: sourceKey,
			itemSearchTerm: '',
			itemDisplayFreeze: true,
			showDescription: false,
		})
	}

	const handleSelectItem = (item) => {
		const fullItem = {
			...item,
			source: state.selectedSource,
		}
		const isAlreadySelected = state.selectedItems.some(
			(selected) => selected.source === state.selectedSource && selected.name === item.name
		)
		if (isAlreadySelected) {
			setState({
				...state,
				selectedItems: state.selectedItems.filter(
					(selected) =>
						!(selected.source === state.selectedSource && selected.name === item.name)
				),
			})
		} else {
			setState({ ...state, selectedItems: [...state.selectedItems, fullItem] })
		}
	}

	const clearAllSelections = () => {
		setState(defaultState)
		localStorage.setItem('selectedItems', JSON.stringify([]))
		localStorage.setItem('outItems', JSON.stringify([]))
		localStorage.setItem('showDescription', 'false')
		localStorage.removeItem('selectedSource')
	}

	const toggleDescription = () => {
		localStorage.setItem('showDescription', (!state.showDescription).toString())
		setState({ ...state, showDescription: !state.showDescription })
	}

	const toggleFreeze = () => {
		setState({ ...state, itemDisplayFreeze: !state.itemDisplayFreeze })
	}

	const addOutItem = () => {
		const trimmed = state.outItemInput.trim()
		if (!trimmed || !state.selectedSource) return
		const exists = state.outItems.some(
			(it) =>
				it.source === state.selectedSource &&
				it.name.toLowerCase() === trimmed.toLowerCase()
		)
		if (exists) {
			setState({ ...state, outItemInput: '' })

			return
		}
		setState({
			...state,
			outItemInput: '',
			outItems: [...state.outItems, { name: trimmed, source: state.selectedSource }],
		})
	}

	const removeOutItem = (name, source) => {
		setState({
			...state,
			outItemInput: '',
			outItems: state.outItems.filter((it) => !(it.name === name && it.source === source)),
		})
	}

	const outItemsForCurrentSource = state.selectedSource
		? state.outItems.filter((it) => it.source === state.selectedSource)
		: []

	const getUniqueSourcesCount = () => {
		const uniqueKeys = new Set(state.selectedItems.map((item) => item.source))

		return uniqueKeys.size
	}

	const handleSettingsClick = (event) => {
		setSettingsAnchor(event.currentTarget)
	}

	const handleSettingsClose = () => {
		setSettingsAnchor(null)
		loadTopLists()
	}

	const settingsOpen = Boolean(settingsAnchor)

	return (
		<Box
			sx={{
				maxWidth: '1400px',
				width: '95%',
				margin: '0 auto',
				padding: '20px',
				minHeight: '100vh',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '20px',
					borderBottom: 1,
					borderColor: 'divider',
					paddingBottom: '10px',
				}}
			>
				<Typography variant="h4">Top Lists</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
					<FormControlLabel
						control={
							<Switch
								checked={isDarkMode}
								onChange={toggleTheme}
								icon={<Brightness7Icon />}
								checkedIcon={<Brightness4Icon />}
							/>
						}
						label={isDarkMode ? 'Dark' : 'Light'}
					/>
					<IconButton onClick={handleSettingsClick} size="large">
						<SettingsIcon />
					</IconButton>
				</Box>
			</Box>

			<Popover
				open={settingsOpen}
				anchorEl={settingsAnchor}
				onClose={handleSettingsClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				PaperProps={{
					sx: {
						maxHeight: '90vh',
						maxWidth: '800px',
						width: '90vw',
					},
				}}
			>
				<Settings onClose={handleSettingsClose} />
			</Popover>

			<Box sx={{ display: 'flex', gap: '30px' }}>
				{/* Data Sources Section */}
				<Box sx={{ flex: 1 }}>
					<Typography variant="h6" sx={{ marginBottom: '15px' }}>
						Select a Data Source
					</Typography>
					{topLists.length > 0 ? (
						<Paper
							sx={{
								border: 1,
								borderColor: 'divider',
								maxHeight: '500px',
								overflowY: 'auto',
							}}
						>
							{topLists.map((topList) => {
								const isSelected = state.selectedSource === topList.key

								return (
									<Box
										key={topList.key}
										onClick={() => handleSelectSource(topList.key)}
										sx={(theme) => ({
											padding: '12px',
											borderBottom: 1,
											borderColor: 'divider',
											cursor: 'pointer',
											transition: 'background-color 0.2s, color 0.2s',
											backgroundColor: isSelected
												? theme.palette.primary.main
												: 'transparent',
											fontWeight: isSelected ? 500 : 400,
											'&:hover': {
												backgroundColor: isSelected
													? theme.palette.primary.dark
													: theme.palette.action.hover,
											},
										})}
									>
										<Typography
											sx={(theme) => ({
												color: isSelected
													? theme.palette.primary.contrastText
													: theme.palette.text.primary,
											})}
										>
											{topList.name}
										</Typography>
										<Typography
											variant="caption"
											sx={(theme) => ({
												color: isSelected
													? theme.palette.primary.contrastText
													: theme.palette.text.secondary,
											})}
										>
											Top {topList.top} • {(topList.values || []).length}{' '}
											items
										</Typography>
									</Box>
								)
							})}
						</Paper>
					) : (
						<Paper sx={{ padding: '20px', textAlign: 'center' }}>
							<Typography color="text.secondary">
								No TopLists found. Click the settings icon to create some.
							</Typography>
						</Paper>
					)}

					<Typography variant="body2" sx={{ margin: '15px 0', fontWeight: 500 }}>
						{state.selectedItems.length} item(s) selected from {getUniqueSourcesCount()}{' '}
						data source(s)
					</Typography>

					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '10px',
							marginTop: '20px',
						}}
					>
						<Button variant="contained" color="primary" onClick={toggleDescription}>
							{state.showDescription ? 'Hide Description' : 'Force Description'}
						</Button>
						<Button variant="contained" color="secondary" onClick={toggleFreeze}>
							{state.itemDisplayFreeze ? 'Unfreeze items' : ' Freeze items'}
						</Button>
						<Button variant="contained" color="error" onClick={clearAllSelections}>
							Clear All
						</Button>
					</Box>
				</Box>

				{/* Items Selection Section */}
				<Box sx={{ flex: 2 }}>
					<Typography variant="h6" sx={{ marginBottom: '15px' }}>
						Select Items
					</Typography>
					{state.selectedSource && currentTopList ? (
						<>
							<TextField
								fullWidth
								placeholder={`Search in ${currentTopList.name}...`}
								value={state.itemSearchTerm}
								onChange={(e) =>
									setState({ ...state, itemSearchTerm: e.target.value })
								}
								sx={{ marginBottom: '20px' }}
							/>
							<Paper
								sx={{
									border: 1,
									borderColor: 'divider',
									height: '600px',
									overflowY: 'auto',
								}}
							>
								{filteredData.length > 0 ? (
									filteredData.map((item, index) => {
										const isSelected = state.selectedItems.some(
											(selected) =>
												selected.source === state.selectedSource &&
												selected.name === item.name
										)
										const uniqueKey = `${state.selectedSource}-${item.name}-${index}`

										return (
											<Box
												key={uniqueKey}
												onClick={() => handleSelectItem(item)}
												sx={(theme) => ({
													padding: '12px',
													borderBottom: 1,
													borderColor: 'divider',
													cursor: 'pointer',
													backgroundColor: isSelected
														? theme.palette.primary.main
														: 'transparent',
													fontWeight: isSelected ? 500 : 400,
													'&:hover': {
														backgroundColor: isSelected
															? theme.palette.primary.dark
															: theme.palette.action.hover,
													},
													display: 'flex',
													justifyContent: 'space-between',
													alignItems: 'center',
												})}
											>
												<Box>
													<Typography
														component="span"
														sx={(theme) => ({
															fontWeight: 600,
															color: isSelected
																? theme.palette.primary.contrastText
																: theme.palette.text.secondary,
														})}
													>
														#{item.rank}{' '}
													</Typography>
													<Typography
														component="span"
														sx={(theme) => ({
															color: isSelected
																? theme.palette.primary.contrastText
																: theme.palette.text.primary,
														})}
													>
														{item.name}
													</Typography>
												</Box>
												{item.value !== undefined && (
													<Typography
														fontWeight="500"
														sx={(theme) => ({
															color: isSelected
																? theme.palette.primary.contrastText
																: theme.palette.primary.main,
														})}
													>
														{typeof item.value === 'number'
															? item.value.toLocaleString('de-CH')
															: item.value}
													</Typography>
												)}
											</Box>
										)
									})
								) : (
									<Box sx={{ padding: '20px', textAlign: 'center' }}>
										<Typography color="text.secondary">
											No items found matching your search.
										</Typography>
									</Box>
								)}
							</Paper>
						</>
					) : (
						<Paper sx={{ padding: '20px', textAlign: 'center' }}>
							<Typography color="text.secondary">
								Please select a data source first
							</Typography>
						</Paper>
					)}
				</Box>

				{/* Custom Items Section */}
				<Box sx={{ flex: 1 }}>
					<Typography variant="h6" sx={{ marginBottom: '15px' }}>
						Add Items not in list
					</Typography>
					{state.selectedSource ? (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
							<Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
								<TextField
									fullWidth
									placeholder="Add custom item"
									value={state.outItemInput}
									onChange={(e) =>
										setState({ ...state, outItemInput: e.target.value })
									}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											addOutItem()
										}
									}}
									disabled={!state.selectedSource}
								/>
								<Button
									variant="contained"
									onClick={addOutItem}
									disabled={!state.selectedSource || !state.outItemInput.trim()}
								>
									Add
								</Button>
							</Box>
							<Paper
								sx={{
									border: 1,
									borderColor: 'divider',
									maxHeight: '500px',
									overflowY: 'auto',
								}}
							>
								{outItemsForCurrentSource.length ? (
									outItemsForCurrentSource.map((it, index) => {
										const uniqueKey = `${it.source}-${it.name}-${index}`

										return (
											<Box
												key={uniqueKey}
												sx={{
													display: 'flex',
													justifyContent: 'space-between',
													alignItems: 'center',
													padding: '10px 12px',
													borderBottom: 1,
													borderColor: 'divider',
												}}
											>
												<Typography>{it.name}</Typography>
												<IconButton
													color="error"
													size="small"
													onClick={() =>
														removeOutItem(it.name, it.source)
													}
												>
													<DeleteOutlineIcon />
												</IconButton>
											</Box>
										)
									})
								) : (
									<Box sx={{ padding: '14px' }}>
										<Typography variant="body2" color="text.secondary">
											No custom items for this source yet.
										</Typography>
									</Box>
								)}
							</Paper>
						</Box>
					) : (
						<Paper sx={{ padding: '20px', textAlign: 'center' }}>
							<Typography color="text.secondary">
								Please select a data source first
							</Typography>
						</Paper>
					)}
				</Box>
			</Box>
		</Box>
	)
}

export default SelectPage
